import { ChangeDetectorRef, Component, ElementRef, HostListener, Inject, Input, LOCALE_ID, SimpleChanges, ViewChild } from '@angular/core';
// Generated by Copilot- Start
import { CalendarEvent, CalendarWeekViewComponent, CalendarUtils, DateAdapter } from 'angular-calendar';
// Generated by Copilot- End

@Component({
  selector: 'app-custom-calendar-week-view',
  templateUrl: './custom-calendar-week-view.component.html',
  styleUrls: ['./custom-calendar-week-view.component.scss']
})
export class CustomCalendarWeekViewComponent extends CalendarWeekViewComponent {
  // Generated by Copilot- Start
  @Input() minimumEventCardWidth = 0;
  @Input() maximumEventCardWidth = 0;
  @Input() stickyBottomScrollbar = false;
  @ViewChild('scrollViewport') scrollViewport!: ElementRef<HTMLDivElement>;
  @ViewChild('bottomScrollViewport') bottomScrollViewport!: ElementRef<HTMLDivElement>;

  calendarContentMinWidth: number | null = null;
  bottomScrollbarWidth = 0;
  bottomScrollbarLeft = 0;
  bottomScrollbarViewportWidth = 0;
  showStickyBottomScrollbar = false;

  private isSyncingScroll = false;
  private readonly defaultTimeLabelWidth = 70;
  // Generated by Copilot- End

  constructor(
    cdr: ChangeDetectorRef,
    utils: CalendarUtils,
    @Inject(LOCALE_ID) locale: string,
    dateAdapter: DateAdapter,
    element: ElementRef<HTMLElement>
  ) {
    super(cdr, utils, locale, dateAdapter, element);
  }

  // Generated by Copilot- Start
  override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    this.queueLayoutSync();
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.queueLayoutSync();
  }

  protected override refreshBody(): void {
    super.refreshBody();
    this.queueLayoutSync();
  }

  protected override refreshAll(): void {
    super.refreshAll();
    this.queueLayoutSync();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.queueLayoutSync();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.queueLayoutSync();
  }

  syncScroll(source: 'viewport' | 'bottom', scrollLeft: number): void {
    if (this.isSyncingScroll) {
      return;
    }

    const target = source === 'viewport'
      ? this.bottomScrollViewport?.nativeElement
      : this.scrollViewport?.nativeElement;

    if (!target) {
      return;
    }

    this.isSyncingScroll = true;
    target.scrollLeft = scrollLeft;
    this.isSyncingScroll = false;
  }

  private queueLayoutSync(): void {
    setTimeout(() => this.updateCalendarLayout());
  }

  private updateCalendarLayout(): void {
    const viewport = this.scrollViewport?.nativeElement;

    if (!viewport) {
      return;
    }

    const dayColumnCount = this.days?.length || this.view?.hourColumns?.length || this.daysInWeek || 1;
    const minDayColumnWidth = this.getMinimumDayColumnWidth();

    this.calendarContentMinWidth = minDayColumnWidth > 0
      ? Math.ceil(this.getTimeLabelWidth() + (dayColumnCount * minDayColumnWidth))
      : null;

    const contentWidth = Math.max(this.calendarContentMinWidth || 0, viewport.scrollWidth);
    const viewportRect = viewport.getBoundingClientRect();

    this.bottomScrollbarWidth = contentWidth;
    this.bottomScrollbarLeft = viewportRect.left;
    this.bottomScrollbarViewportWidth = viewportRect.width;
    this.showStickyBottomScrollbar = this.stickyBottomScrollbar && contentWidth > viewport.clientWidth;

    if (this.bottomScrollViewport?.nativeElement) {
      this.bottomScrollViewport.nativeElement.scrollLeft = viewport.scrollLeft;
    }

    this.cdr.markForCheck();
  }

  private getMinimumDayColumnWidth(): number {
    const dayEventCardWidth = this.getDayEventCardWidth();

    if (!dayEventCardWidth) {
      return 0;
    }

    const widths: number[] = [];

    (this.view?.hourColumns || []).forEach((column) => {
      column.events.forEach((event) => {
        if (event.width > 0) {
          widths.push(event.width);
        }
      });
    });

    if (widths.length === 0) {
      return dayEventCardWidth;
    }

    const smallestWidth = Math.min(...widths);
    return Math.ceil((dayEventCardWidth * 100) / smallestWidth);
  }

  getTimeEventWidth(eventWidth: number): string {
    if (this.shouldUseFixedDayEventLanes()) {
      return `${this.getNormalizedMaximumEventCardWidth()}px`;
    }

    if (
      this.maximumEventCardWidth > 0
      && this.daysInWeek === 1
      && this.isNearFullWidthEvent(eventWidth)
    ) {
      return `${this.getNormalizedMaximumEventCardWidth()}px`;
    }

    if (
      this.minimumEventCardWidth > 0
      && this.daysInWeek === 1
      && this.isNearFullWidthEvent(eventWidth)
    ) {
      return `${this.minimumEventCardWidth}px`;
    }

    return `${eventWidth}%`;
  }

  getTimeEventMaxWidth(eventWidth: number): string | null {
    if (this.shouldUseFixedDayEventLanes()) {
      return `${this.getNormalizedMaximumEventCardWidth()}px`;
    }

    if (
      this.maximumEventCardWidth > 0
      && this.daysInWeek === 1
    ) {
      return `${this.getNormalizedMaximumEventCardWidth()}px`;
    }

    if (
      this.minimumEventCardWidth > 0
      && this.daysInWeek === 1
      && this.isNearFullWidthEvent(eventWidth)
    ) {
      return `${this.minimumEventCardWidth}px`;
    }

    return null;
  }

  getTimeEventLeftPercent(eventLeft: number, eventWidth: number): number | null {
    if (this.shouldUseFixedDayEventLanes() && eventWidth > 0) {
      return null;
    }

    return eventLeft;
  }

  getTimeEventLeftPx(eventLeft: number, eventWidth: number): number | null {
    return this.getTimeEventLeftPxForEvent(eventLeft, eventWidth);
  }

  getTimeEventLeftPxForEvent(eventLeft: number, eventWidth: number, event?: CalendarEvent): number | null {
    if (!this.shouldUseFixedDayEventLanes()) {
      return null;
    }
    if (event?.meta?.overlapLaneIndex !== undefined) {
      return (event.meta.overlapLaneIndex as number) * this.getNormalizedMaximumEventCardWidth();
    }
    return this.getTimeEventLeftPxInternal(eventLeft, eventWidth);
  }

  private getTimeEventLeftPxInternal(eventLeft: number, eventWidth: number): number | null {
    if (eventWidth <= 0) {
      return null;
    }
    const laneIndex = Math.round(eventLeft / eventWidth);
    return laneIndex * this.getNormalizedMaximumEventCardWidth();
  }

  private shouldUseFixedDayEventLanes(): boolean {
    return this.daysInWeek === 1 && this.maximumEventCardWidth > 0;
  }

  private getDayEventCardWidth(): number {
    if (this.shouldUseFixedDayEventLanes()) {
      return this.getNormalizedMaximumEventCardWidth();
    }

    return this.minimumEventCardWidth;
  }

  private isNearFullWidthEvent(eventWidth: number): boolean {
    return eventWidth >= 95;
  }

  private getNormalizedMaximumEventCardWidth(): number {
    if (this.minimumEventCardWidth > 0) {
      return Math.max(this.maximumEventCardWidth, this.minimumEventCardWidth);
    }

    return this.maximumEventCardWidth;
  }

  private getTimeLabelWidth(): number {
    const timeLabelColumn = this.element.nativeElement.querySelector<HTMLElement>('.cal-time-label-column');
    return Math.ceil(timeLabelColumn?.getBoundingClientRect().width || this.defaultTimeLabelWidth);
  }
  // Generated by Copilot- End
}
